import React from "react";
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepButton from "@material-ui/core/StepButton";
import Chip from "@material-ui/core/Chip";
import StepConnector from "@material-ui/core/StepConnector";
import withStyles from "@material-ui/core/styles/withStyles";
import StepLabel from "@material-ui/core/StepLabel";
import Badge from "@material-ui/core/Badge";
import { GlobalHotKeys } from "react-hotkeys";
import classNames from "classnames";
import { FootballState } from "@prestosports/prestostats-core/Football";
import { useFootballContext } from "../../../state/football-play/context";
import PlayStepContent from "./PlayStepContent";
import playStepperStyles from "../styles/PlayStepper.styles";
import gameMuiTheme from "../../layout/styles/GameMuiTheme";
import { useAppContext } from "../../../state/app/context";

const StepperKeyListener = ({ goStepBack, goStepForward }) => {
	const keyMap = {
		NAVIGATION: ["Right", "Left"]
	};

	const handlers = {
		NAVIGATION: (event) => {
			if (event.key.indexOf("Left") >= 0) {
				goStepBack();
			} else if (event.key.indexOf("Right") >= 0) {
				goStepForward();
			}
		}
	};

	return <GlobalHotKeys keyMap={keyMap} handlers={handlers} allowChanges />;
};

const PlayStepper = () => {
	const classes = playStepperStyles();
	const {
		state,
		startTransition,
		revertToTransition,
		setActiveStep,
		goStepBack,
		goStepForward
	} = useFootballContext();
	const currentPlay = state.currentPlay();
	const activeStep = state.activeStep();
	const steps = state.steps();
	const lastPlayStep = state.lastStep();

	const { state: appState } = useAppContext();
	const editPlay = appState.editPlay();
	const confirmSave = appState.confirmSave();
	const isSubmitting = appState.isSubmitting();

	const handleTransition = (transitionName, fromHistoryIndex) => {
		if (!isSubmitting) {
			const { nextTransition, isResult } =
				steps.find((s) => s.index === activeStep) || {};
			const shouldCancelTransition = nextTransition === transitionName && isResult;

			if (shouldCancelTransition) {
				revertToTransition(fromHistoryIndex);
			} else {
				startTransition(transitionName, fromHistoryIndex);
			}
		}
	};

	const isDriveStart = () => {
		const [lastStep] = steps.slice(-1);
		return lastStep.state === FootballState.StartDrive;
	};

	const handleStep = (index) => {
		setActiveStep(index);
	};

	const editMode =
		!!editPlay && !!currentPlay && editPlay?.id === currentPlay?.id;

	const goStepEdit = (forward = true) => {
		const editableStep = steps.findIndex(
			(step, i) => step.isEditable() && (forward ? i > activeStep : i < activeStep)
		);
		if (editableStep >= 0) handleStep(editableStep);
	};

	const ChevronConnector = withStyles({
		root: {
			flex: "none",
			padding: "0 10px"
		},
		line: {
			color: gameMuiTheme.palette.gameMode.secondary,
			border: "none",
			transform: "scaleY(1.5)",
			"&::before": {
				content: "'>'"
			}
		}
	})(StepConnector);

	return (
		<div className={classes.root}>
			{activeStep > 0 && !isDriveStart() && (
				<Stepper
					nonLinear
					activeStep={activeStep}
					className={classes.stepper}
					connector={<ChevronConnector />}
				>
					{steps.map((step, index) => {
						const stepEditable = editMode && step.isEditable();
						const editableOrNA = stepEditable || !editMode;
						return (
							<Step key={step.index} style={{ padding: 0 }}>
								<StepButton
									onClick={() => {
										if (editableOrNA) {
											handleStep(index);
										}
									}}
									icon={null}
									className={classNames(classes.stepButton, {
										[classes.stepButtonDisabled]: !editableOrNA
									})}
								>
									<StepLabel
										classes={{
											label: classes.stepLabel,
											active: classes.stepLabelActive
										}}
									>
										{stepEditable ? (
											<>
												{activeStep === index && (
													<Chip
														color="secondary"
														variant="default"
														clickable
														label={step.displayName}
													/>
												)}
												{activeStep === index || (
													<Chip
														color="secondary"
														variant="outlined"
														clickable
														label={step.displayName}
													/>
												)}
											</>
										) : (
											<Badge
												color="primary"
												variant="dot"
												invisible={step.isComplete()}
												overlap="rectangular"
											>
												{step.displayName}
											</Badge>
										)}
									</StepLabel>
								</StepButton>
							</Step>
						);
					})}
				</Stepper>
			)}

			<PlayStepContent
				play={currentPlay}
				steps={steps}
				stepIndex={activeStep}
				onSelectTransition={handleTransition}
				lastStep={lastPlayStep}
			/>
			{!confirmSave && (
				<StepperKeyListener
					goStepBack={editMode ? () => goStepEdit(false) : goStepBack}
					goStepForward={editMode ? goStepEdit : goStepForward}
				/>
			)}
		</div>
	);
};

export default PlayStepper;
